import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useFacility } from '../../context/FacilityContext';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { db } from '../../firebase/config';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import DevelopmentOnly from '../../Components/Dashboard/DevelopmentOnly/DevelopmentOnly';

function Groups() {
  const { facilityId, _ } = useFacility();
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({
    groupName: '',
    staffs: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [allFacilityStaff, setAllFacilityStaff] = useState([]);
  const [editingGroup, setEditingGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState({});
  const [infoAlert, setInfoAlert] = useState('');
  const restInfoAlert = () => {
    setTimeout(() => {
      setInfoAlert('');
    }, 3000);
  };
  const handleLoading = (state) => {
    setIsLoading(state);
  };
  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
  };
  const handleEditGroup = (group) => {
    setEditingGroup(true);
    setSelectedGroup(group);
    setFormData({
      groupName: group.groupName,
      staffs: group.staffs,
    });
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (editingGroup) {
      // update group
      try {
        setIsLoading(true);
        setInfoAlert('');
        await updateDoc(doc(db, 'groups', selectedGroup.id), {
          ...formData,
          facilityId: facilityId,
        });
      } catch (error) {
        console.error('Error updating group:', error);
      } finally {
        getGroupsData();
        setEditingGroup(false);
        setFormData({
          groupName: '',
          staffs: [],
        });
        setSelectedGroup(null);
        setIsLoading(false);
        setInfoAlert('Successfully Updated Group!');
        restInfoAlert();
      }
    } else {
      try {
        setIsLoading(true);
        setInfoAlert('');
        await addDoc(collection(db, 'groups'), {
          ...formData,
          facilityId: facilityId,
        });
        setFormData({
          groupName: '',
          staffs: [],
        });
      } catch (error) {
        console.error('Error adding group:', error);
      } finally {
        getGroupsData();
        setIsLoading(false);
        setInfoAlert('Successfully Added Group!');
        restInfoAlert();
      }
    }
    setIsLoading(false);
  };
  const handleDeleteGroup = async () => {
    setIsLoading(true);
    setInfoAlert('');
    try {
      await deleteDoc(doc(db, 'groups', selectedGroup.id));
    } catch (error) {
      console.error('Error deleting group:', error);
    } finally {
      getGroupsData();
      setEditingGroup(false);
      setFormData({
        groupName: '',
        staffs: [],
      });
      setIsLoading(false);
      setInfoAlert('Successfully Deleted Group!');
      setSelectedGroup(null);
      restInfoAlert();
    }
  };

  const getGroupsData = async () => {
    const q = query(
      collection(db, 'groups'),
      where('facilityId', '==', facilityId),
    );

    try {
      const querySnapshot = await getDocs(q);
      let groupsData = [];
      querySnapshot.forEach((doc) => {
        groupsData.push({ ...doc.data(), id: doc.id });
      });
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups data:', error);
    }
  };
  const getStaffData = async () => {
    const q = query(
      collection(db, 'users'),
      where('facilities', 'array-contains', facilityId),
      where('staff', '==', true),
    );

    try {
      const querySnapshot = await getDocs(q);
      let staffs = [];
      querySnapshot.forEach((doc) => {
        staffs.push({ ...doc.data(), id: doc.id });
      });
      setAllFacilityStaff(staffs);
    } catch (error) {
      console.error('Error fetching staff data:', error);
    }
  };
  useEffect(() => {
    if (!facilityId) return;
    getGroupsData();
    getStaffData();
  }, [facilityId]);

  return (
    <DashboardLayout
      facilityId={facilityId}
      isLoading={isLoading}
      setIsLoading={handleLoading}
    >
      <DevelopmentOnly fallback={<p>This module is under development.</p>}>
        {infoAlert && (
          <Alert
            severity="success"
            onClose={() => {
              setInfoAlert('');
            }}
          >
            {infoAlert}
          </Alert>
        )}
        <Box sx={{ maxWidth: 1280 }}>
          {
            // selecte Group from dropdown
            groups.length > 0 && (
              <Box
                sx={{
                  width: {
                    sm: '100%', // for medium screens and up
                    md: '50%', // for large screens and up
                  },
                }}
              >
                <Typography variant="h6">Select Group</Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="groupLabel">Select Group</InputLabel>
                  <Select
                    value={selectedGroup}
                    onChange={(e) => handleEditGroup(e.target.value)}
                    labelId="groupLabel"
                    label="Select Group"
                    placeholder="Select Group"
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.id} value={group}>
                        {group.groupName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )
          }
          {editingGroup ? (
            <h2 style={{ marginBottom: '10px', marginTop: '20px' }}>
              Edit Group
            </h2>
          ) : (
            <h2 style={{ marginBottom: '10px', marginTop: '20px' }}>
              Create New Group
            </h2>
          )}

          <form onSubmit={handleSubmit}>
            <Box>
              <TextField
                label="Group Name"
                type="text"
                value={formData.groupName}
                onChange={handleChange('groupName')}
                margin="normal"
                autoComplete="groupName"
                required
                fullWidth
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="staffLabel">Staffs In Group</InputLabel>
                <Select
                  multiple
                  value={formData.staffs}
                  onChange={handleChange('staffs')}
                  labelId="staffLabel"
                  label="Staffs In Group"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const staff = allFacilityStaff.find(
                          (staff) => staff.id === value,
                        );
                        return (
                          <Chip
                            key={value}
                            label={staff ? staff.email : value}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {allFacilityStaff.map((staffPermission) => (
                    <MenuItem
                      key={staffPermission.id}
                      value={staffPermission.id}
                    >
                      {staffPermission.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mr: 6 }}
            >
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
            {editingGroup && (
              <>
                <Button
                  onClick={() => handleDeleteGroup()}
                  variant="contained"
                  color="error"
                  sx={{ mr: 3 }}
                >
                  Delete
                </Button>
                <Button
                  onClick={() => {
                    setEditingGroup(false);
                    setFormData({
                      groupName: '',
                      staffs: [],
                    });
                    setSelectedGroup(null);
                  }}
                  variant="contained"
                  color="primary"
                >
                  Cancel
                </Button>
              </>
            )}
          </form>
        </Box>
      </DevelopmentOnly>
    </DashboardLayout>
  );
}

Groups.propTypes = {};

export default Groups;
